// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import {
  Button,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  tableCellClasses,
  Switch,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridValueGetterParams, GridActionsCellItem } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { getTenantById, updateTenantStatus } from "lib/Api/Tenants";
import { useContext, useEffect, useState } from "react";
import LocalDataTable from "examples/Tables/LocalDataTable";
import DefaultCell from "examples/Tables/components/DefaultCell";
import { Download, Edit } from "@mui/icons-material";
import moment from "moment";
import { sendReminderApi } from "lib/Api/Authentication";
import { LoginFormContext } from "context/loginFormCtx";
import TenantAdmins from "./tenant-admin";
import DraggableDialog from "components/draggagle-dialog";
import EditTenantDetails from "./EditTenantDetails";
import MDBadge from "components/MDBadge";
import MDLink from "components/MDLink";

// Custom components
import TenantRequestsSection from "components/SubscriptionExtendRequests/TenantRequestsSection";

function TenantsDetails() {
  const { id } = useParams();

  const { setNotification, showMessageNotification } = useContext(LoginFormContext);

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [tenantDetails, setTenantDetails] = useState(null);

  // state for handling stripe customer availability
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [tenantData, setTenantData] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [userCount, setUserCount] = useState(null);
  const [disableReminderBtn, setDisableReminderBtn] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null); // State for subscription data

  const dataTableData = {
    columns: [
      {
        Header: "Subscription",
        accessor: "subscription",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value || "-"} />,
      },
      {
        Header: "Amount Paid",
        accessor: "amount_paid",
        filterInputType: "number",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Date of Issue",
        accessor: "period_start",
        filterInputType: "date",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Due Date",
        accessor: "period_end",
        filterInputType: "date",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Status",
        accessor: "status",
        filterInputType: "select",
        sort: true,
        filterOptions: [
          { value: "draft", label: "Draft" },
          { value: "open", label: "Open" },
          { value: "paid", label: "Paid" },
          { value: "uncollectible", label: "Uncollectible" },
          { value: "void", label: "Void" },
        ],
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Actions",
        accessor: "invoice_pdf",
        sort: false,
        Cell: ({ value }) => (
          <MDBox>
            <Tooltip title="Download Invoice" placement="top">
              <IconButton
                onClick={() => {
                  const URL = value;
                  window.open(URL, "_blank");
                }}
              >
                <Download />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],

    rows: [],
  };

  const [isLoading, setIsLoading] = useState(false);

  const [tableData, setTableData] = useState({
    columns: dataTableData.columns,
    rows: [],
  });

  const getTenantDetails = async (tenantId) => {
    try {
      setIsLoading(true);
      const response = await getTenantById(tenantId);
      if (response?.status === 200) {
        const responseData = response?.data?.data;
        if (!responseData?.isStripeCustomerAvailable) {
          setShowErrorMessage(true);
        }
        setTenantDetails(responseData);
        setTenantData(responseData?.tenantData);
        setInvoices(responseData?.invoices);
        setUpcomingInvoice(responseData?.upcomingInvoice);
        setUserCount(responseData?.userCount);
        setSubscriptionData(responseData?.subscriptionData); // Set subscription data from API response

        const tempRows = responseData?.invoices?.data?.map((invoice) => {
          return {
            subscription: invoice.subscription,
            amount_paid: `$${(invoice.amount_paid / 100).toFixed(2)}`,
            period_start: moment(invoice.period_start * 1000).format("MM/DD/YYYY"),
            period_end: moment(invoice.period_end * 1000).format("MM/DD/YYYY"),
            // make status first letter uppercase
            status: invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
            invoice_pdf: invoice.invoice_pdf,
          };
        });

        setTableData({
          ...tableData,
          rows: tempRows,
        });
      } else {
        // navigate("/tenants");
      }
    } catch (error) {
      console.log("Error (getTenantDetails): ", error);
      // navigate("/tenants");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReminder = async () => {
    setDisableReminderBtn(true);
    try {
      const response = await sendReminderApi(upcomingInvoice);

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "Reminder sent successfully",
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message,
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.message || "Something went wrong!",
        open: true,
      });
    }
    setDisableReminderBtn(false);
  };

  useEffect(() => {
    getTenantDetails(id);
  }, [id]);

  const handleStatusChange = async () => {
    showMessageNotification({
      title: "Are you sure?",
      text: "You want to change the status of this tenant?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, change it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await updateTenantStatus(tenantData?.id, {
          status: tenantData?.status ? 0 : 1,
        });
        if (response?.status === 200) {
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
          await getTenantDetails(id);
        } else {
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      }
    });
  };

  const getAddressString = (addr) => {
    let address = addr?.address_line_1 || "";
    if (addr?.address_line_2) address += `, ${addr?.address_line_2}`;
    if (addr?.city) address += `, ${addr?.city}`;
    if (addr?.country) address += `, ${addr?.country}`;
    if (addr?.zip_code) address += `, ${addr?.zip_code}`;
    return address;
  };

  // Helper function to format free trial information
  const getFreeTrialInfo = () => {
    if (!subscriptionData?.is_free_trial) return null;
    
    return {
      startDate: moment(subscriptionData.purchase_date).format("MMM DD, YYYY"),
      endDate: moment(subscriptionData.end_date).format("MMM DD, YYYY"),
      daysRemaining: moment(subscriptionData.end_date).diff(moment(), 'days'),
      isExpired: moment(subscriptionData.end_date).isBefore(moment()),
      planName: subscriptionData?.plan?.name || "Free Trial Plan",
      planDescription: subscriptionData?.plan?.description,
      perUserCharges: subscriptionData?.plan?.per_user_charges || "0.00",
      planDuration: subscriptionData?.plan?.duration,
      planDurationType: subscriptionData?.plan?.plan_duration_type,
      planType: subscriptionData?.plan?.plan_type
    };
  };

  return (
    <CustomPageLayout>
      {isLoading ? (
        <Card sx={{ my: 3 }}>
          <MDBox display="flex"justifyContent="center" alignItems="center" height="calc(100vh - 200px)">
            <CircularProgress size={60} />
          </MDBox>
        </Card>
      ) : (
        <>
          {tenantDetails && (
            <MDBox py={3}>
              <Card>
                <Stack lineHeight={1} spacing={2} sx={{ p: 3 }}>
                  <Grid container sx={{ pb: 3 }} justifyContent={"space-between"}>
                    <Grid item>
                      <MDTypography variant="h5" fontWeight="medium">
                        {tenantData?.company_name || "Tenant Details"}
                      </MDTypography>
                      <MDTypography variant="button" color="text">
                        Tenant details and its related information.
                      </MDTypography>
                    </Grid>
                    <MDTypography variant="button" color="text">
                      <Switch
                        inputProps={{
                          "aria-label": "statuc switch",
                        }}
                        checked={tenantData?.status === 1 ? true : false}
                        onChange={handleStatusChange}
                        color="default"
                      />
                    </MDTypography>
                  </Grid>

                  {/* Tenant Details */}
                  <Stack>
                    <Paper variant="outlined" sx={{ p: "20px", position: "relative" }}>
                      <Stack spacing={1}>
                        <Stack spacing={1}>
                          <Stack sx={{ typography: "h5" }}>Tenant Details</Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ typography: "body2" }}>
                          <Stack sx={{ color: "text.primary" }}>Company Name :</Stack>
                          <Stack sx={{ color: "text.secondary" }}>{tenantData?.company_name}</Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ typography: "body2" }}>
                          <Stack sx={{ color: "text.primary" }}>Status :</Stack>
                          <Stack
                            sx={{ color: tenantData?.status ? "primary.main" : "text.disabled" }}
                          >
                            {tenantData?.status ? "Active" : "Inactive"}
                          </Stack>
                        </Stack>

                        {tenantDetails?.billingAddress?.rows?.map((addr) => (
                          <Stack direction="row" spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>
                              {addr.address_type === 1 ? "Billing Address" : "Company Address"} :
                            </Stack>
                            <Stack sx={{ color: "text.secondary", wordBreak: "break-word" }}>
                              {getAddressString(addr)}
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>

                      <Tooltip title="Edit Tenant Details" placement="top">
                        <IconButton
                          sx={{ position: "absolute", top: 0, right: 0 }}
                          onClick={() => {
                            setOpenEditDialog(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </Stack>

                  {/* Tenant Overview */}
                  <Stack>
                    <Paper variant="outlined" sx={{ p: "20px", textTransform: "capitalize" }}>
                      <Stack spacing={1}>
                        <Stack spacing={1}>
                          <Stack sx={{ typography: "h5" }}>Tenant Overview</Stack>
                        </Stack>

                        <Stack spacing={10} direction="row">
                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Users count</Stack>
                            <Stack sx={{ color: "primary.main" }}>
                              {/* {tenantData?.tenantUsers?.length} */}
                              {(userCount?.activeUsers || 0) + (userCount?.inactiveUsers || 0)}
                            </Stack>
                          </Stack>

                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Branches count</Stack>
                            <Stack sx={{ color: "primary.main" }}>{tenantDetails?.branches || 0}</Stack>
                          </Stack>

                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Invoices count</Stack>
                            <Stack sx={{ color: "primary.main" }}>{invoices?.data?.length || 0}</Stack>
                          </Stack>

                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Active Users</Stack>
                            <Stack sx={{ color: "primary.main" }}>{userCount?.activeUsers || 0}</Stack>
                          </Stack>

                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Inactive Users</Stack>
                            <Stack sx={{ color: "primary.main" }}>{userCount?.inactiveUsers || 0}</Stack>
                          </Stack>

                          <Stack spacing={1} sx={{ typography: "body2" }}>
                            <Stack sx={{ color: "text.primary" }}>Outside Organization Users</Stack>
                            <Stack sx={{ color: "primary.main" }}>
                              {userCount?.outSideOfOrganization || 0}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>

                  {/* Subscription Overview — Stripe-linked subscriptions only */}
                  {subscriptionData?.subscription_id && (
                    <Stack>
                      <Paper variant="outlined" sx={{ p: "20px" }}>
                        <Stack spacing={1}>
                          <Stack sx={{ typography: "h5" }}>Subscription Overview</Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                              <ListItem>
                                <ListItemText
                                  primary="ID"
                                  secondary={
                                    subscriptionData?.subscription_id ? (
                                      showErrorMessage ? (
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                          {subscriptionData.subscription_id}
                                        </Typography>
                                      ) : (
                                        <MDLink
                                          to={`/subscriptions/${subscriptionData.subscription_id}`}
                                          sx={{ wordBreak: "break-all" }}
                                        >
                                          <Typography variant="subtitle2">
                                            {subscriptionData.subscription_id}
                                          </Typography>
                                        </MDLink>
                                      )
                                    ) : (
                                      "N/A"
                                    )
                                  }
                                  primaryTypographyProps={{ variant: "h6", color: "#566681" }}
                                  secondaryTypographyProps={{
                                    variant: "subtitle2",
                                    color: "text.secondary",
                                    component: "div",
                                  }}
                                />
                              </ListItem>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                              <ListItem>
                                <ListItemText
                                  primary="Customer"
                                  secondary={
                                    tenantDetails?.userData
                                      ? `${tenantDetails.userData.first_name || ""} ${
                                          tenantDetails.userData.last_name || ""
                                        }`.trim() || "N/A"
                                      : "N/A"
                                  }
                                  primaryTypographyProps={{ variant: "h6", color: "#566681" }}
                                  secondaryTypographyProps={{
                                    variant: "subtitle2",
                                    color: "text.secondary",
                                  }}
                                />
                              </ListItem>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                              <ListItem>
                                <ListItemText
                                  primary="Email"
                                  secondary={tenantDetails?.userData?.email || "N/A"}
                                  primaryTypographyProps={{ variant: "h6", color: "#566681" }}
                                  secondaryTypographyProps={{
                                    variant: "subtitle2",
                                    color: "text.secondary",
                                  }}
                                />
                              </ListItem>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                              <ListItem>
                                <ListItemText
                                  primary="Current"
                                  secondary={
                                    subscriptionData?.purchase_date && subscriptionData?.end_date
                                      ? `${moment(subscriptionData.purchase_date).format(
                                          "MM-DD-YYYY"
                                        )} to ${moment(subscriptionData.end_date).format(
                                          "MM-DD-YYYY"
                                        )}`
                                      : "N/A"
                                  }
                                  primaryTypographyProps={{ variant: "h6", color: "#566681" }}
                                  secondaryTypographyProps={{
                                    variant: "subtitle2",
                                    color: "text.secondary",
                                  }}
                                />
                              </ListItem>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>
                    </Stack>
                  )}

                  {/* Free Trial or Upcoming Invoice */}
                  {subscriptionData?.is_free_trial ? (
                    <Stack spacing={3}>
                      {/* Trial Details */}
                      <Stack>
                        <Paper variant="outlined" sx={{ p: "20px" }}>
                          <Stack spacing={2}>
                            <Stack
                              direction="row"
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="h5">Trial Details</Typography>
                              <MDBadge
                                badgeContent={getFreeTrialInfo()?.isExpired ? "Expired" : "Active"}
                                variant="contained"
                                container
                                color={getFreeTrialInfo()?.isExpired ? "error" : "success"}
                              />
                            </Stack>

                            <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Start Date
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {getFreeTrialInfo()?.startDate}
                                </Typography>
                              </Stack>

                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  End Date
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {getFreeTrialInfo()?.endDate}
                                </Typography>
                              </Stack>

                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Status
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={getFreeTrialInfo()?.isExpired ? "error.main" : "success.main"}
                                  fontWeight="medium"
                                >
                                  {getFreeTrialInfo()?.isExpired
                                    ? "Trial Expired"
                                    : `${getFreeTrialInfo()?.daysRemaining} days remaining`}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Stack>

                      {/* Plan Details */}
                      <Stack>
                        <Paper variant="outlined" sx={{ p: "20px" }}>
                          <Stack spacing={2}>
                            <Typography variant="h5">Plan Details</Typography>

                            <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Plan Name
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {getFreeTrialInfo()?.planName}
                                </Typography>
                              </Stack>

                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Regular Price
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  ${getFreeTrialInfo()?.perUserCharges} per user
                                </Typography>
                              </Stack>

                              <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Duration
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {getFreeTrialInfo()?.planDuration}{" "}
                                  {getFreeTrialInfo()?.planDurationType === 1
                                    ? "Day(s)"
                                    : getFreeTrialInfo()?.planDurationType === 2
                                    ? "Month(s)"
                                    : "Year(s)"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Stack>
                  ) : upcomingInvoice ? (
                    <Stack>
                      <Paper variant="outlined" sx={{ p: "20px" }}>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="h5">Upcoming Invoice</Typography>

                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleSendReminder}
                              disabled={disableReminderBtn}
                            >
                              Send reminder
                            </Button>
                          </Stack>

                          <Stack spacing={1} sx={{ mt: 5 }}>
                            <Stack sx={{ typography: "h6" }}>Summary</Stack>

                            <Divider />

                            <TableBody
                              sx={{
                                [`& .${tableCellClasses.root}`]: {
                                  borderBottom: "none",
                                },
                              }}
                            >
                              <TableRow>
                                <TableCell>Customer</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.primary">
                                    {upcomingInvoice?.customer_name}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell>Customer Email</TableCell>
                                <TableCell>{upcomingInvoice?.customer_email}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Stack>

                          <Stack spacing={1} sx={{ mt: 5 }}>
                            <Table>
                              <TableBody
                                sx={{
                                  [`& .${tableCellClasses.root}`]: {
                                    typography: "h6",
                                  },
                                }}
                              >
                                <TableCell sx={{ width: "70%" }}>Description</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Unit Price</TableCell>
                                <TableCell>Amount</TableCell>
                              </TableBody>

                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <Stack spacing={1} direction="row" sx={{ fontWeight: 700 }}>
                                      <Stack>
                                        {upcomingInvoice?.lines?.data[0]?.plan?.nickname || "Plan"}
                                      </Stack>
                                      <Stack>{`(${moment(
                                        upcomingInvoice?.period_start * 1000
                                      ).format("DD MMM YYYY")} - ${moment(
                                        upcomingInvoice?.period_end * 1000
                                      ).format("DD MMM YYYY")})`}</Stack>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{upcomingInvoice?.lines?.data[0]?.quantity}</TableCell>
                                  <TableCell>
                                    $
                                    {(
                                      upcomingInvoice?.lines?.data[0]?.price?.unit_amount / 100
                                    )?.toFixed(2)}
                                  </TableCell>
                                  <TableCell>
                                    $
                                    {(
                                      upcomingInvoice?.lines?.data[0]?.quantity *
                                      (upcomingInvoice?.lines?.data[0]?.price?.unit_amount / 100)
                                    )?.toFixed(2)}
                                  </TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell>
                                    <Stack direction="column" rowGap={2}>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        Subtotal
                                      </Typography>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        Total excluding tax
                                      </Typography>

                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        Total
                                      </Typography>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        Amount due
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="column" rowGap={2}>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        ${(upcomingInvoice?.subtotal / 100)?.toFixed(2) ?? "-"}
                                      </Typography>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        $
                                        {(
                                          upcomingInvoice?.subtotal_excluding_tax / 100
                                        )?.toFixed(2) ?? "-"}
                                      </Typography>

                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        ${(upcomingInvoice?.total / 100)?.toFixed(2) ?? "-"}
                                      </Typography>
                                      <Typography sx={{ fontWeight: 500, fontSize: "medium" }}>
                                        ${(upcomingInvoice?.amount_due / 100)?.toFixed(2) ?? "-"}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  ) : null}

                  {/* Invoices - Only show for paid subscriptions */}
                  {!subscriptionData?.is_free_trial && !showErrorMessage && (
                    <Stack>
                      <Paper variant="outlined" sx={{ p: "20px", textTransform: "capitalize" }}>
                        <Stack spacing={1}>
                          <Stack spacing={1}>
                            <Stack sx={{ typography: "h5" }}>Tenant's Invoices</Stack>
                          </Stack>

                          <Stack>
                            <LocalDataTable
                              isLoading={isLoading}
                              table={tableData}
                              isCSVExport
                              defaultSorting={[{ id: "period_end", desc: true }]}
                              onRefresh={() => getTenantDetails(id)}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  )}
                </Stack>
              </Card>


              <DraggableDialog
                dialogTitle="Edit Tenant Details"
                open={openEditDialog}
                onClose={() => {
                  setOpenEditDialog(false);
                }}
                dialogContent={
                  <EditTenantDetails
                    tenantDetails={{
                      company_name: tenantDetails?.tenantData?.company_name,
                      billingAddress: tenantDetails?.billingAddress?.rows?.sort(
                        (a, b) => a.address_type - b.address_type
                      ),
                    }}
                    getTenantDetails={() => {
                      getTenantDetails(id);
                      setOpenEditDialog(false);
                    }}
                  />
                }
                maxWidth="xl"
              />
            </MDBox>
          )}
        </>
      )}
    </CustomPageLayout>
  );
}

export default TenantsDetails;
